import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Star, Award, Clock, Plus, X, Crown, BookOpen, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [location, setLocation] = useState('');
  const [skillsOffered, setSkillsOffered] = useState([]);
  const [helpNeeded, setHelpNeeded] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [newHelp, setNewHelp] = useState('');
  const [newAvailability, setNewAvailability] = useState('');
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setLocation(user.profile.location || '');
      setSkillsOffered(user.profile.skills_offered || []);
      setHelpNeeded(user.profile.help_needed || []);
      setAvailability(user.profile.availability || []);
      fetchReviews();
    }
  }, [user]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${user.id}`, { withCredentials: true });
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/users/profile`, {
        username,
        profile: {
          skills_offered: skillsOffered,
          help_needed: helpNeeded,
          availability,
          location
        }
      }, { withCredentials: true });

      toast.success(t('notifications.profileUpdated'));
      await checkAuth();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !skillsOffered.includes(newSkill.trim())) {
      setSkillsOffered([...skillsOffered, newSkill.trim()]);
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setSkillsOffered(skillsOffered.filter(s => s !== skill));
  };

  const addHelp = () => {
    if (newHelp.trim() && !helpNeeded.includes(newHelp.trim())) {
      setHelpNeeded([...helpNeeded, newHelp.trim()]);
      setNewHelp('');
    }
  };

  const removeHelp = (help) => {
    setHelpNeeded(helpNeeded.filter(h => h !== help));
  };

  const addAvailability = () => {
    if (newAvailability.trim() && !availability.includes(newAvailability.trim())) {
      setAvailability([...availability, newAvailability.trim()]);
      setNewAvailability('');
    }
  };

  const removeAvailability = (avail) => {
    setAvailability(availability.filter(a => a !== avail));
  };

  const getTrustLevelColor = (level) => {
    const colors = {
      'newcomer': 'bg-gray-100 text-gray-600 border-gray-300',
      'trusted': 'bg-blue-100 text-blue-600 border-blue-300',
      'expert': 'bg-purple-100 text-purple-600 border-purple-300'
    };
    return colors[level] || colors.newcomer;
  };

  return (
    <Layout>
      <div data-testid="profile-page" className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('profile.title')}</h1>

        {/* Profile Header Card */}
        <Card className="p-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex flex-col items-center">
              <Avatar className="h-32 w-32 mb-4">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-teal-100 text-teal-600 text-4xl">
                  {user?.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900">{user?.name}</h2>
                <p className="text-gray-500">@{user?.username || user?.email.split('@')[0]}</p>
              </div>
            </div>

            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-teal-50 rounded-lg">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-teal-500" />
                  <p className="text-2xl font-bold text-teal-600">{user?.credits_balance}</p>
                  <p className="text-sm text-gray-600">{t('dashboard.credits')}</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <Star className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                  <p className="text-2xl font-bold text-yellow-600">{user?.reputation?.rating_avg.toFixed(1)}</p>
                  <p className="text-sm text-gray-600">{t('dashboard.rating')}</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Award className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                  <p className="text-2xl font-bold text-purple-600">{user?.reputation?.total_ratings}</p>
                  <p className="text-sm text-gray-600">{t('profile.reviews')}</p>
                </div>
              </div>

              <div className="flex justify-center">
                <Badge className={`px-4 py-2 text-base border ${getTrustLevelColor(user?.reputation?.trust_level)}`}>
                  {user?.reputation?.trust_level} Member
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Edit Profile Card */}
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-6">{t('profile.editProfile')}</h3>
          <div className="space-y-6">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('profile.username')}</label>
              <Input
                data-testid="username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('profile.username')}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">{t('profile.location')}</label>
              <Input
                data-testid="location-input"
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={t('profile.locationPlaceholder')}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">{t('profile.skillsOffered')}</label>
              <div className="flex gap-2 mb-3">
                <Input
                  data-testid="skill-input"
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder={t('profile.addSkill')}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                />
                <Button
                  data-testid="add-skill-button"
                  type="button"
                  onClick={addSkill}
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {skillsOffered.map((skill, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-teal-50 text-teal-600 flex items-center gap-2">
                    {skill}
                    <button onClick={() => removeSkill(skill)} className="hover:text-teal-800">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">{t('profile.helpNeeded')}</label>
              <div className="flex gap-2 mb-3">
                <Input
                  data-testid="help-input"
                  type="text"
                  value={newHelp}
                  onChange={(e) => setNewHelp(e.target.value)}
                  placeholder={t('profile.addHelp')}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHelp())}
                />
                <Button
                  data-testid="add-help-button"
                  type="button"
                  onClick={addHelp}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {helpNeeded.map((help, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-cyan-50 text-cyan-600 flex items-center gap-2">
                    {help}
                    <button onClick={() => removeHelp(help)} className="hover:text-cyan-800">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">{t('profile.availability')}</label>
              <div className="flex gap-2 mb-3">
                <Input
                  data-testid="availability-input"
                  type="text"
                  value={newAvailability}
                  onChange={(e) => setNewAvailability(e.target.value)}
                  placeholder={t('profile.addAvailability')}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAvailability())}
                />
                <Button
                  data-testid="add-availability-button"
                  type="button"
                  onClick={addAvailability}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {availability.map((avail, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-purple-50 text-purple-600 flex items-center gap-2">
                    {avail}
                    <button onClick={() => removeAvailability(avail)} className="hover:text-purple-800">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <Button
              data-testid="save-profile-button"
              onClick={handleSaveProfile}
              disabled={loading}
              className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 text-lg rounded-lg"
            >
              {loading ? t('profile.saving') : t('profile.saveProfile')}
            </Button>
          </div>
        </Card>

        {/* Reviews Card */}
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-6">{t('profile.reviews')}</h3>
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <p className="text-gray-500 text-center py-8">{t('profile.noReviews')}</p>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${
                            i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-gray-500">
                      {new Date(review.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-gray-700">{review.comment}</p>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Availability Calendar */}
        <AvailabilityCalendar />

        {/* Creator Section (only if creator_status) */}
        {user?.creator_status && (
          <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Creator Profile</h3>
                <p className="text-sm text-purple-600 dark:text-purple-400">
                  {user.creator_type || 'Creator'} • Sharing knowledge with the community
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg">
                <BookOpen className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">0</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Courses</p>
              </div>
              <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg">
                <Users className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">0</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Students</p>
              </div>
              <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg">
                <Award className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  £{(user.wallet_balance_gbp || 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Earnings</p>
              </div>
            </div>

            <Button
              onClick={() => navigate('/creator')}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            >
              Go to Creator Dashboard
            </Button>
          </Card>
        )}
      </div>
    </Layout>
  );
}